import React, { useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ConsentContext } from '@context/ConsentContext'
import { SurveyContext } from '@context/SurveyContext'
import { APP_TYPE, AppType, isCanadaApp, isLiteraryApp, isQuebecApp, isSanteApp } from '@models/AppType'

import './LocaleSwitcher.scss'

export interface OwnProps {}

const languages: Record<AppType, { locale: string; name: string }[]> = {
  [AppType.ELECTIONS_USA]: [
    {
      locale: 'en',
      name: 'English',
    },
    {
      locale: 'es',
      name: 'Español',
    },
    {
      locale: 'fr',
      name: 'Français',
    },
  ],
  [AppType.ELECTIONS_CANADA]: [
    {
      locale: 'en',
      name: 'English',
    },
    {
      locale: 'fr',
      name: 'Français',
    },
  ],
  [AppType.ELECTIONS_QUEBEC]: [
    {
      locale: 'en',
      name: 'English',
    },
    {
      locale: 'fr',
      name: 'Français',
    },
  ],
  [AppType.LQUE]: [
    {
      locale: 'en',
      name: 'English',
    },
    {
      locale: 'fr',
      name: 'Français',
    },
  ],
  [AppType.SANTE]: [
    {
      locale: 'en',
      name: 'English',
    },
    {
      locale: 'fr',
      name: 'Français',
    },
  ],
}

export function LocaleSwitcher() {
  const { i18n } = useTranslation('Home')

  const { hasConsented } = useContext(ConsentContext)
  const { fetchSurvey, currentSurveyId } = useContext(SurveyContext)

  const currentLocale = (i18n.language ?? 'fr').substring(0, 2)

  // If app is ecan25/quebec/sante and the current locale is Spanish, change it to French.
  useEffect(() => {
    if ((isCanadaApp || isSanteApp || isQuebecApp) && currentLocale === 'es') {
      i18n.changeLanguage('fr')
    }
  }, [isCanadaApp, isSanteApp, isQuebecApp, currentLocale])

  const handleChange = useCallback(
    (locale: string) => {
      i18n.changeLanguage(locale)

      if (hasConsented && currentSurveyId) {
        fetchSurvey(currentSurveyId)
      }

      window.location.reload()
    },
    [hasConsented, currentSurveyId, fetchSurvey, i18n],
  )

  const generateLocaleSwitcher = useCallback(() => {
    const links = []
    for (const locale of languages[APP_TYPE]) {
      const currentLocal = i18n.language.split('-')[0]

      if (currentLocal === locale.locale) {
        continue
      }

      links.push(
        <a key={locale.locale} className="LocaleSwitcher-link" onClick={() => handleChange(locale.locale)}>
          {locale.name}
        </a>,
      )
    }

    return links
  }, [i18n.language, handleChange])

  return <div className={`LocaleSwitcher${isLiteraryApp ? ' BOOKSELLERS' : ''}`}>{generateLocaleSwitcher()}</div>
}

export default LocaleSwitcher
